// frontend/src/context/EntitlementsContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import entitlementsService from '../services/entitlementsService';

const EntitlementsContext = createContext(null);

const EMPTY = {};

// Fail-open default used when a consumer sits outside the provider (e.g. unit
// tests that render a screen in isolation): nothing is hidden.
const FAIL_OPEN = {
  loading: false,
  error: null,
  product: EMPTY,
  resolved: EMPTY,
  isProductEnabled: () => true,
  isEntitled: () => true,
  refresh: () => {},
};

/**
 * Loads the current tenant's entitlements once authenticated and exposes the
 * two-gate model to the app (task #3782). Every check is **fail-open**: while
 * loading, on error, or for an unknown feature the answer is "enabled", so a
 * backend hiccup can never black out the whole menu. The authoritative denial
 * still lives in the backend (endpoints return 403).
 */
export const EntitlementsProvider = ({ children }) => {
  const { authenticated } = useAuth();
  const [product, setProduct] = useState(EMPTY);
  const [resolved, setResolved] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!authenticated) {
      setProduct(EMPTY);
      setResolved(EMPTY);
      return;
    }
    setLoading(true);
    try {
      const data = await entitlementsService.getEntitlements();
      setProduct(data?.product || EMPTY);
      setResolved(data?.resolved || EMPTY);
      setError(null);
    } catch (e) {
      // Fail open: keep empty maps so every gate resolves to enabled.
      setError(e);
      setProduct(EMPTY);
      setResolved(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({
      loading,
      error,
      product,
      resolved,
      // Gate 0 ÔÇö product ("does this product have the module?"). Used for menu
      // and route visibility. Only an explicit `false` hides.
      isProductEnabled: (feature) => product[feature] !== false,
      // Gate 0 AND Gate 1 for this tenant. Only an explicit `false` denies.
      isEntitled: (feature) => resolved[feature] !== false,
      refresh: load,
    }),
    [loading, error, product, resolved, load],
  );

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
};

export const useEntitlements = () => useContext(EntitlementsContext) || FAIL_OPEN;

/** Convenience hook: resolved (product AND subscription) access for one feature. */
export const useEntitlement = (feature) => useEntitlements().isEntitled(feature);

/**
 * Renders `children` only when the feature is entitled, else `fallback`.
 * `level="product"` checks module existence only (ignores subscription tier).
 */
export const EntitlementGate = ({
  feature,
  level = 'resolved',
  children,
  fallback = null,
}) => {
  const { isEntitled, isProductEnabled } = useEntitlements();
  const ok = level === 'product' ? isProductEnabled(feature) : isEntitled(feature);
  return ok ? children : fallback;
};

export default EntitlementsContext;
